import React, { useState, useCallback, useRef } from 'react';
import { Upload, Loader2, ArrowRight, Mail } from 'lucide-react';
import { analyzeLoop } from '../lib/AudioAnalyzer';
import { classifyGenre } from '../lib/GenreClassifier';
import { track } from '../lib/analytics';

// Standalone free-tool page — deliberately NOT part of the Studio bundle.
// Reuses the exact same in-browser analysis the paid app uses: no upload to
// a server, no account, nothing leaves the visitor's machine.

const C = {
  bg: '#DDD9D3', card: '#fff', ink: '#292524', body: '#57534e', muted: '#78716c',
  accent: '#c9593a', accentHover: '#a84830', border: '#c9c4bd',
};
const display = "'Barlow Condensed', sans-serif";
const body = "'Outfit', -apple-system, sans-serif";

function fmtDuration(s) {
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function BpmKeyFinder() {
  const [status, setStatus] = useState('idle'); // idle | analyzing | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | sending | sent
  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setStatus('analyzing');
    setError('');
    track('tool_analyze_started');
    try {
      const analysis = await analyzeLoop(file);
      const genre = classifyGenre(analysis);
      setResult({ ...analysis, genre });
      setStatus('done');
      track('tool_analyze_completed');
    } catch (err) {
      console.error(err);
      setError("Couldn't analyze that file. Try an MP3, WAV, AIFF, or OGG under 50MB.");
      setStatus('error');
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setEmailStatus('sending');
    try {
      await fetch('/api/subscribe-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'tool' }),
      });
    } catch (_) { /* non-fatal */ }
    track('tool_email_captured');
    setEmailStatus('sent');
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: body, color: C.ink }}>
      {/* header */}
      <header style={{ borderBottom: `1px solid ${C.border}`, background: 'rgba(221,217,211,0.92)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ fontFamily: display, fontWeight: 600, fontSize: '1.2rem', textTransform: 'lowercase', color: C.ink, textDecoration: 'none' }}>
            arrangement<span style={{ color: C.accent }}>lab</span>
          </a>
          <a href="/app.html" style={{ background: C.accent, color: '#fff', padding: '0.55rem 1.1rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
            Try free for 14 days
          </a>
        </div>
      </header>

      <main style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem 4rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.muted, marginBottom: '1.5rem' }}>
          <a href="/" style={{ textDecoration: 'none' }}>Home</a> / BPM &amp; Key Finder
        </p>
        <h1 style={{ fontFamily: display, fontWeight: 600, fontSize: 'clamp(2.2rem,5vw,3.2rem)', textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1.1, marginBottom: '0.75rem' }}>
          Free BPM &amp; key finder
        </h1>
        <p style={{ fontSize: '1.1rem', color: C.body, maxWidth: '55ch', marginBottom: '2rem' }}>
          Drop in a loop or a track. Get the BPM, musical key, and genre back instantly —
          no signup, no upload to a server. Everything runs in your browser.
        </p>

        {/* drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? C.accent : C.border}`,
            borderRadius: 16, padding: '3rem 2rem', textAlign: 'center', cursor: 'pointer',
            background: dragOver ? 'rgba(201,89,58,0.06)' : C.card, marginBottom: '1.5rem',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <input
            ref={inputRef} type="file" accept="audio/*" style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {status === 'analyzing' ? (
            <>
              <Loader2 style={{ width: 32, height: 32, color: C.accent, margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }} />
              <p style={{ fontWeight: 600 }}>Analyzing…</p>
            </>
          ) : (
            <>
              <Upload style={{ width: 32, height: 32, color: C.muted, margin: '0 auto 1rem' }} />
              <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Drop an audio file, or click to browse</p>
              <p style={{ fontSize: '0.85rem', color: C.muted }}>MP3, WAV, AIFF, or OGG · under 50MB</p>
            </>
          )}
        </div>

        {error && (
          <p style={{ color: C.accent, fontSize: '0.9rem', marginBottom: '2rem' }}>{error}</p>
        )}

        {result && status === 'done' && (
          <>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.5rem', marginBottom: '2rem' }}>
              <p style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '1rem' }}>{result.fileName} · {fmtDuration(result.duration)}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: '0.2rem' }}>BPM</p>
                  <p style={{ fontSize: '1.6rem', fontWeight: 700 }}>{Math.round(result.bpm)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: '0.2rem' }}>Key</p>
                  <p style={{ fontSize: '1.6rem', fontWeight: 700 }}>{result.key}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: '0.2rem' }}>Genre</p>
                  <p style={{ fontSize: '1.6rem', fontWeight: 700 }}>{result.genre.primary.name}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: '0.2rem' }}>Bars</p>
                  <p style={{ fontSize: '1.6rem', fontWeight: 700 }}>{result.bars}</p>
                </div>
              </div>
            </div>

            {/* upsell panel */}
            <div style={{ background: '#292524', color: '#fafaf9', borderRadius: 16, padding: '1.75rem', marginBottom: '2rem' }}>
              <h2 style={{ fontFamily: display, fontWeight: 600, fontSize: '1.4rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                Now map the whole song
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '1.25rem', maxWidth: '50ch' }}>
                arrangementLab takes this exact analysis and generates a full arrangement around it —
                every section, production notes included.
              </p>
              <a href="/app.html" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.accent, color: '#fff', padding: '0.8rem 1.5rem', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>
                Build my arrangement <ArrowRight style={{ width: 16, height: 16 }} />
              </a>
            </div>

            {/* optional email capture */}
            {emailStatus !== 'sent' ? (
              <form onSubmit={onSubmitEmail} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '3rem' }}>
                <input
                  type="email" required placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ flex: '1 1 220px', padding: '0.7rem 1rem', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: '0.9rem', fontFamily: body }}
                />
                <button type="submit" disabled={emailStatus === 'sending'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1.5px solid ${C.ink}`, color: C.ink, padding: '0.7rem 1.1rem', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontFamily: body }}>
                  <Mail style={{ width: 15, height: 15 }} /> Email me arrangement tips
                </button>
              </form>
            ) : (
              <p style={{ color: C.muted, marginBottom: '3rem' }}>You're in — check your inbox.</p>
            )}
          </>
        )}

        {status === 'idle' && (
          <div style={{ marginTop: '3rem' }}>
            <h2 style={{ fontFamily: display, fontWeight: 600, fontSize: '1.3rem', textTransform: 'uppercase', marginBottom: '0.75rem' }}>How it works</h2>
            <p style={{ color: C.body, marginBottom: '0.5rem' }}>This tool runs the same audio analysis engine used inside arrangementLab — FFT-based tempo detection and Krumhansl-Kessler key profiling — entirely in your browser tab.</p>
            <p style={{ color: C.body }}>Nothing is uploaded anywhere. Close the tab and it's gone.</p>
          </div>
        )}
      </main>

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: '2rem 1.5rem', fontSize: '0.85rem', color: C.muted }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <a href="/" style={{ marginRight: 20, textDecoration: 'none' }}>Home</a>
          <a href="/structure/" style={{ marginRight: 20, textDecoration: 'none' }}>Song Structures</a>
          <a href="/terms.html" style={{ marginRight: 20, textDecoration: 'none' }}>Terms</a>
          <a href="/privacy.html" style={{ textDecoration: 'none' }}>Privacy</a>
        </div>
      </footer>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
